import { Injectable } from '@angular/core';
import { GolfEvent } from '../models/GolfEvent';
import { LeagueWeek } from '../models/LeagueWeek';
import { LeagueRound } from '../models/LeagueRound';
import { firstValueFrom } from 'rxjs';
import { CreateGolfEventRequest } from '../models/creategolfeventrequest';
import { HttpClient } from '@angular/common/http';
import { CreateLeagueWeekRequest } from '../models/createleagueweekrequest';
import { UpdateLeagueWeekRequest } from '../models/updateleagueweekrequest';
import { UpdateGolfEventRequest } from '../models/updategolfeventrequest';
import { ConfigurationService } from './configuration.service';
import { GolfEventField } from '../models/golfeventfield';
import { PostScoreRequest } from '../models/postScoreRequest';
import { AuthenticationService } from './authentication.service';
import { Team } from '../models/team';
import { CreateTeamRequest } from '../models/createTeamRequest';
import { UpdateTeamRequest } from '../models/updateTeamRequest';
import { WeeklyLeaderboard } from '../models/weeklyleaderboard';
import { LeagueLeaderboard } from '../models/leagueleaderboard';
import { Player } from '../models/Player';

@Injectable({
  providedIn: 'root'
})
export class GolfEventService {

  constructor(private http:HttpClient, private configService:ConfigurationService, private authenticationService:AuthenticationService) { 
    this.baseUrl = this.configService.getApiEndpoint();
  }

  baseUrl:string;

  async createEvent(request:CreateGolfEventRequest):Promise<number> {
    let endpoint = `${this.baseUrl}/golfevent`;
    let result = await firstValueFrom(this.http.post<number>(endpoint, request));
    return result;
  }

  async updateEvent(request:UpdateGolfEventRequest):Promise<void> {
    let endpoint = `${this.baseUrl}/golfevent`;
    await firstValueFrom(this.http.patch(endpoint, request));
  }

  async createLeagueWeek(request:CreateLeagueWeekRequest):Promise<number> {
    let endpoint = `${this.baseUrl}/leagueweek`;
    let result = await firstValueFrom(this.http.post<number>(endpoint, request));
    return result;
  }

  async updateLeagueWeek(request:UpdateLeagueWeekRequest):Promise<void> {
    let endpoint = `${this.baseUrl}/leagueweek`;
    await firstValueFrom(this.http.patch(endpoint, request));
  }

  async searchEvents():Promise<Array<GolfEvent>> {
    let endpoint = `${this.baseUrl}/golfevents`;
    let result = await firstValueFrom(this.http.post<GolfEvent[]>(endpoint, {}));
    return result;
  }

  async getLeagueWeeks(eventId:number):Promise<Array<LeagueWeek>> {
    let endpoint = `${this.baseUrl}/leagueweeks`;
    let weeks = await firstValueFrom(this.http.post<LeagueWeek[]>(endpoint, { golfEventId: eventId }));
    return weeks;
  }

  async getGolfEventById(eventId:number):Promise<GolfEvent> {
    let events = await this.searchEvents();
    let eventsFound = events.filter(evt => evt.id.toString() === eventId.toString()); //I HATE the toString() here but there is a bug I can't get resolved.
    return eventsFound[0];
  }

  async searchField(eventId:number, hasPaid:boolean|null):Promise<Array<GolfEventField>> {
    let endpoint = `${this.baseUrl}/field`;
    let args:any = { golfEventId: eventId };
    if(hasPaid !== null) {
      args.hasPaid = hasPaid;
    }
    let result = await firstValueFrom(this.http.post<GolfEventField[]>(endpoint, args));
    return result;
  }

  async updateFieldHasPaid(fieldId:number, hasPaid:boolean):Promise<void> {
    let endpoint = `${this.baseUrl}/haspaid`;
    await firstValueFrom(this.http.patch(endpoint, { fieldId: fieldId, hasPaid: hasPaid }));
  }

  generatePaymentLink(event:GolfEvent):string {
    let link = this.configService.getPaymentServiceLink();
    let username = this.configService.getPaymentServiceUsername();

    link = link.replace('{username}', username);
    const note = encodeURIComponent(event.eventName);//.replace(/\+/g, '%20'); //Trying to get the symbols out of the note in Venmo but it's breaking on iPhone for some reason.
    link = link.replace('{tag}', note);
    link += "&amount=" + event.entryFee;
    return link;
  }

  async getLeagueRounds(leagueWeekId:number):Promise<Array<LeagueRound>> {
    let endpoint = `${this.baseUrl}/leaguerounds`;
    let rounds = await firstValueFrom(this.http.post<Array<LeagueRound>>(endpoint, { leagueWeekId: leagueWeekId }));
    return rounds;
  }

  async postScores(scores:Array<PostScoreRequest>):Promise<boolean> {
    let email = '';
    if(this.authenticationService.loginResult && this.authenticationService.loginResult.player)
      email = this.authenticationService.loginResult.player.email;

    let scoresWithEmail:Array<PostScoreRequest> = [];
    scores.forEach(s => {
      s.email = email;
      scoresWithEmail.push(s);
    });

    let endpoint = `${this.baseUrl}/leagueround`;
    try {
      let result = await firstValueFrom(this.http.post<boolean>(endpoint, scoresWithEmail));
      return result;
    } catch (err: any) {
      const message = err?.error?.error || err?.error || 'Unable to post score.';
      alert(typeof message === 'string' ? message : 'Unable to post score.');
      throw err;
    }
  }

  async searchTeams(golfEventId:number):Promise<Array<Team>> {
    let endpoint = `${this.baseUrl}/teams`;
    let rounds = await firstValueFrom(this.http.post<Array<Team>>(endpoint, { golfEventId: golfEventId }));
    return rounds;
  }

  async createTeam(request:CreateTeamRequest):Promise<void> {
    let endpoint = `${this.baseUrl}/team`;
    await firstValueFrom(this.http.post(endpoint, request));
  }

  async updateTeam(request:UpdateTeamRequest):Promise<void> {
    let endpoint = `${this.baseUrl}/team`;
    await firstValueFrom(this.http.patch(endpoint, request));
  }

  async deleteTeam(teamId:number, deletedByPlayerId:number):Promise<void> {
    let endpoint = `${this.baseUrl}/team`;
    await firstValueFrom(this.http.request('DELETE', endpoint, {
      body: { teamId, deletedByPlayerId }
    }));
  }

  async getWeeklyLeaderboard(leagueWeekId:number):Promise<Array<WeeklyLeaderboard>> {
    let endpoint = `${this.baseUrl}/weeklyleaderboard`;
    return await firstValueFrom(this.http.post<Array<WeeklyLeaderboard>>(endpoint, { leagueWeekId: leagueWeekId }));
  }

  async getLeagueLeaderboard(golfEventId:number):Promise<Array<LeagueLeaderboard>> {
    let endpoint = `${this.baseUrl}/leagueleaderboard`;
    return await firstValueFrom(this.http.post<Array<LeagueLeaderboard>>(endpoint, { golfEventId: golfEventId }));
  }

  async deleteLeagueRound(roundId:number):Promise<void> {
    let email = '';
    if(this.authenticationService.loginResult && this.authenticationService.loginResult.player)
      email = this.authenticationService.loginResult.player.email;

    let request = { email: email, leagueRoundId: roundId };
    let endpoint = `${this.baseUrl}/leagueround`;
    await firstValueFrom(this.http.request('DELETE', endpoint, { body: request }));
  }
  
  async getPlayersNotInField(golfEventId:number):Promise<Array<Player>> {
    let endpoint = `${this.baseUrl}/notinfield`;
    return await firstValueFrom(this.http.post<Array<Player>>(endpoint, { golfEventId: golfEventId }));
  }

  async addPlayersToField(golfEventId:number, playerIds:Array<number>):Promise<void> {
    let endpoint = `${this.baseUrl}/addtofield`;
    await firstValueFrom(this.http.post(endpoint, { golfEventId: golfEventId, playerIds: playerIds }));
  }
}